import CryptoES from 'crypto-es';

import {
  callApi,
  getRequest,
  handleError,
  handleResponse,
  postRequest,
  patchRequest,
} from '@helpers/api';

export * from './user';

const userSignupRequest = agent => payload => agent.post('auth/user/', payload);
const getOrgRequest = agent => id => agent.get(`auth/organization/${id}/`);
const disableUserRequest = agent => (id, payload) => agent.patch(`auth/user/${id}/`, payload);
const enableUserRequest = agent => (id, payload) => agent.patch(`auth/user/${id}/`, payload);
const updateUserRequest = agent => (id, payload) => agent.put(`auth/user/${id}/`, payload);
const inviteUserRequest = agent => payload => agent.post('auth/userInvitation/', payload);

/** @deprecated */
export const disableUser = async id =>
  callApi(disableUserRequest)(id, { is_active: false }).then(handleResponse).catch(handleError);

/** @deprecated */
export const enableUser = async id =>
  callApi(enableUserRequest)(id, { is_active: true }).then(handleResponse).catch(handleError);

export const updateUser = async (id, { password, ...payload }) => {
  let passwordHash = CryptoES.SHA512(password);
  passwordHash = passwordHash.toString(CryptoES.enc.Hex);

  return callApi(updateUserRequest)(id, { password: passwordHash, ...payload })
    .then(handleResponse)
    .catch(handleError);
};

export const getOrg = async orgId =>
  callApi(getOrgRequest)(orgId).then(handleResponse).catch(handleError);

export const userSignUp = async ({ firstName, lastName, email, password }) => {
  let passwordHash = CryptoES.SHA512(password);
  passwordHash = passwordHash.toString(CryptoES.enc.Hex);

  return callApi(userSignupRequest)({
    first_name: firstName,
    last_name: lastName,
    email,
    password: passwordHash,
  })
    .then(handleResponse)
    .catch(handleError);
};

/**
 * Requests a password change given an email, token and
 * new password.
 *
 * * Note: Endpoint will always return 200, even if email
 * is invalid. The only time it'll return 400 is when
 * the email IS valid but the token has expired.
 *
 * @param {Object} payload
 * @returns {Promise<unknown>}
 */
export const changePassRequest = async ({ email, token, password }) => {
  let passwordHash = CryptoES.SHA512(password);
  passwordHash = passwordHash.toString(CryptoES.enc.Hex);
  postRequest(`auth/passwordLost/${email}/${token}/`, { password: passwordHash });
};

/**
 * Verifies a password change token and email.
 *
 * Note: Endpoint will always return 200, even if email
 * is invalid. The only time it'll return 400 is when
 * the email IS valid but the token has expired.
 *
 * @param {Object} payload
 * @returns {Promise<unknown>}
 */
export const verifyChangePassRequest = ({ email, token }) =>
  getRequest(`auth/passwordLost/${email}/${token}/`);

/**
 * Change the password for the current user
 * @param {Object} payload
 * @param {string} payload.email user email
 * @param {string} payload.token password reset token
 * @param {string} payload.password new password
 * @param {string} payload.confirm_password new password confirmation
 * @returns {Promise<*>}
 */
export const changePasswordRequest = payload => patchRequest('auth/passwordChange/', payload);

export const inviteUser = async payload =>
  callApi(inviteUserRequest)(payload).then(handleResponse).catch(handleError);

/**
 * Request password reset for a user account.
 * @param {string} email - Email address associated with account.
 * @returns {Promise<unknown>}
 */
export const forgotPassword = email => postRequest('auth/passwordLost/', { email });

/**
 * Request account data
 * @param {string} id - Account id associated with account
 * @returns {Promise<unknown>}
 */
export const getUserAccount = id => getRequest(`auth/account/${id}/`);
